<template>
  <div
    v-if="!image"
    class="flex items-center justify-center h-full w-full bg-secondary-100"
    :class="roundedClass">
    <button
      :id="triggerId"
      ref="buttonRef"
      class="flex flex-col items-center justify-center gap-1 h-[77px]"
      @click="openPopover">
      <FolderUp class="text-secondary-975 h-7 w-7" />
      <span class="text-secondary-975 text-xs">
        {{ t('blocksSettings.pairing.add_image') }}
      </span>
      <span dir="rtl" class="text-secondary-600 text-xs">
        {{ t('blocksSettings.pairing.max_size') }}
      </span>
    </button>
  </div>
  <div
    v-else
    :id="triggerId"
    ref="buttonRef"
    :class="imageDivClass"
    @click="openPopover">
    <img :src="image" :class="imageClass" />
    <div
      class="w-full h-full opacity-0 group-hover/imageedit:opacity-100 transition-opacity duration-300 absolute inset-0 flex justify-center items-center bg-[#0006]">
      <SquarePen class="text-white" :size="20" />
    </div>
  </div>
  <BasePopover
    v-model="isOpen"
    :target="buttonRef"
    placement="left"
    :trigger-id="triggerId"
    class="z-10"
    @close="closePopover">
    <AddImagePopover :id="id" v-model:src="image" @close="closePopover" />
  </BasePopover>
</template>

<script setup lang="ts">
import { ref, computed, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import { PairingItemFormat } from '@amit/types';
import { FolderUp, SquarePen } from 'lucide-vue-next';
import AddImagePopover from '../../../../builder/src/components/AddImagePopover.vue';
import { BasePopover } from '@nwire/amit-design-system';
import { nanoid } from 'nanoid';

const { t } = useI18n();

const props = defineProps({
  id: {
    type: String,
    default: '',
  },
  format: {
    type: String as PropType<PairingItemFormat>,
    default: PairingItemFormat.ImageText,
  },
});

const image = defineModel<string>({ required: true });
const isOpen = ref(false);
const buttonRef = ref<HTMLDivElement>();
const triggerId = ref(`trigger-${nanoid()}`);

const imageDivClass = computed(() => {
  return [
    'w-full relative cursor-pointer group/imageedit',
    props.format.includes('text') ? 'h-[78px]' : 'h-full',
  ];
});

const imageClass = computed(() => {
  const itemClass = ['w-full h-full object-cover relative p-0.5'];
  if (!props.format.includes('text')) {
    itemClass.push('rounded-md');
  }
  if (props.format === PairingItemFormat.ImageText) {
    itemClass.push('rounded-t-md');
  } else {
    itemClass.push('rounded-b-md');
  }
  return itemClass;
});

const roundedClass = computed(() => {
  if (!props.format.includes('text')) return 'rounded-md';
  if (props.format === PairingItemFormat.ImageText) {
    return 'rounded-t-md';
  } else {
    return 'rounded-b-md';
  }
});

const openPopover = () => {
  isOpen.value = true;
};

const closePopover = () => {
  isOpen.value = false;
};
</script>
